import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  InputGroup,
  Row,
  Col,
  Spinner,
  ListGroup,
  Accordion,
  Nav,
  Tab,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import api from "../../../services/api"; // <-- adjust if needed
import { fetchCategorySummaries } from "../../../services/apiService";
import {
  PLACE_CIRCLE_HINT,
  PLACE_CIRCLE_MAX,
  PLACE_CIRCLE_MIN,
  placeCircleInvalid,
  withPlaceCircle,
} from "../../../utils/placeCircle";
import { toast } from "react-toastify";

const DEFAULT_VIDEO_LANGUAGE = "en";
// Add a row here to offer another language; the rest of the editor adapts.
const VIDEO_LANGUAGES = [{ code: "en", label: "English" }];
const DUPLICATE_DURATION_OPTIONS = Array.from({ length: 10 }, (_, index) => ({
  label: `${index + 1} ${index === 0 ? "Night" : "Nights"} & ${index + 2} Days`,
  nights: index + 1,
  days: index + 2,
}));

// The trip editor's tabs, in the order they are shown.
const TRIP_EDIT_TABS = [
  { key: "basics", label: "Basics", icon: "bi-card-heading" },
  { key: "overview", label: "Overview", icon: "bi-card-text" },
  { key: "itinerary", label: "Itinerary", icon: "bi-calendar3" },
  { key: "pickups", label: "Pickups", icon: "bi-geo-alt" },
  { key: "faqs", label: "FAQs & Terms", icon: "bi-question-circle" },
  { key: "photos", label: "Photos", icon: "bi-images" },
];

const formatRupees = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `₹${amount.toLocaleString("en-IN")}` : `₹${value}`;
};

const fileNameFromUrl = (url) =>
  decodeURIComponent(String(url || "").split("/").pop() || "");

const youtubeVideoId = (url) => {
  const match = String(url || "").match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : "";
};

export default function TripsManager() {
  const [query, setQuery] = useState("");
  const [show, setShow] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [activeToggleId, setActiveToggleId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]); // [{id, name, slug?}]
  // What the homepage card shows for each live trip, keyed by trip id; null until it loads.
  const [cardPrices, setCardPrices] = useState(null);

  const loadCardPrices = async () => {
    try {
      const summaries = await fetchCategorySummaries();
      const prices = {};
      (summaries || []).forEach((category) =>
        (category.trips || []).forEach((trip) => {
          prices[trip.id] = trip.price;
        })
      );
      setCardPrices(prices);
    } catch {
      setCardPrices(null);
    }
  };

  const load = async () => {
    setLoading(true);
    loadCardPrices();
    try {
      const [{ data: trips }, { data: cats }] = await Promise.all([
        api.get("/api/admin/trips"),
        api.get("/api/admin/categories"), // expect id, name; slug optional
      ]);
      setRows((trips || []).map((trip) => ({ ...trip, active: trip.active !== false })));
      setCategories(cats);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const orderedRows = [...rows].sort(
      (a, b) =>
        (a.categoryName || "").localeCompare(b.categoryName || "") ||
        (a.state || "").localeCompare(b.state || "") ||
        (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
        (a.id ?? 0) - (b.id ?? 0)
    );
    if (!q) return orderedRows;
    return orderedRows.filter((t) =>
      [
        t.title,
        t.duration,
        t.path,
        t.availability,
        t.categoryName ?? "",
        t.state ?? "",
        t.departureLocation ?? "",
        t.departureTime ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const groupedTrips = useMemo(() => {
    const groups = [];
    const byKey = new Map();

    filtered.forEach((trip) => {
      const key = String(trip.categoryId ?? "uncategorized");
      if (!byKey.has(key)) {
        const group = {
          key,
          name: trip.categoryName || "Uncategorized",
          states: [],
          tripCount: 0,
          statesByKey: new Map(),
        };
        byKey.set(key, group);
        groups.push(group);
      }
      const group = byKey.get(key);
      const stateName = trip.state?.trim() || "State not set";
      const stateKey = stateName.toLowerCase();
      if (!group.statesByKey.has(stateKey)) {
        const stateGroup = { key: `${key}-${stateKey}`, name: stateName, trips: [] };
        group.statesByKey.set(stateKey, stateGroup);
        group.states.push(stateGroup);
      }
      group.statesByKey.get(stateKey).trips.push(trip);
      group.tripCount += 1;
    });

    return groups.map(({ statesByKey, ...group }) => group);
  }, [filtered]);

  const tripCountByCategory = useMemo(() => rows.reduce((counts, trip) => {
    const key = String(trip.categoryId ?? "");
    if (key) counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {}), [rows]);

  const disabledCategoryIds = useMemo(
    () => new Set((categories || []).filter((category) => category.active === false).map((category) => String(category.id))),
    [categories]
  );

  const upsert = async (payload) => {
    if (payload.id) {
      const { data } = await api.put(`/api/admin/trips/${payload.id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
    } else {
      const { data } = await api.post(`/api/admin/trips`, payload);
      setRows((prev) => [data, ...prev]);
    }
  };

  const duplicateTrip = async (source, payload) => {
    const { data } = await api.post(`/api/admin/trips/${source.id}/duplicate`, payload);
    setRows((prev) => [data, ...prev]);
    setDuplicateSource(null);
    toast.success(payload.copyDynamicPricing ? "Trip duplicated. Dynamic pricing copied as disabled draft." : "Trip duplicated.");
  };

  const toggleTripActive = async (trip) => {
    if (!trip?.id) return;
    const nextActive = trip.active === false;
    try {
      setActiveToggleId(trip.id);
      const { data } = await api.patch(`/api/admin/trips/${trip.id}/active`, { active: nextActive });
      setRows((prev) => prev.map((row) => (row.id === data.id ? data : row)));
      toast.success(nextActive ? "Trip enabled" : "Trip disabled");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Failed to update trip status");
    } finally {
      setActiveToggleId(null);
    }
  };

  // 1) Replace your onDelete with this version
  const onDelete = async (trip) => {
    if (!trip) return;
    const ok = window.confirm(
      `Delete this trip AND all its images from S3?\n\nTrip: ${trip.title}\nPrefix: trips/${trip.categoryId}/${trip.path}/`
    );
    if (!ok) return;

    try {
      // 1) Delete all S3 objects under trips/{categoryId}/{path}/
      const prefix = `${trip.categoryId}/${trip.path}/`; // UI sends WITHOUT leading "trips/", backend will normalize
      await api.post("/api/admin/assets/delete-prefix", { prefixes: [prefix] });

      // OPTIONAL: if your icon/cover may live outside the per-trip folder (old scheme),
      // you could attempt to delete those individually if you want:
      // for (const url of [trip.imageUrl, trip.coverUrl].filter(Boolean)) {
      //   try { await api.post("/api/admin/assets/delete", { url }); } catch {}
      // }

      // 2) Delete the DB record
      await api.delete(`/api/admin/trips/${trip.id}`);

      // 3) Update UI
      setRows((prev) => prev.filter((r) => r.id !== trip.id));
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || e?.message || "Delete failed");
    }
  };

  // Allow modal to push partial updates to the table without closing it
  const onPatched = (updated) => {
    if (!updated?.id) return;
    setRows((prev) =>
      prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r))
    );
  };

  const openEditor = (trip) => {
    setEditing(trip);
    setShow(true);
  };

  // The price on the homepage card: worked out for 12 people from the trip's pricing, or the
  // typed price when the trip has no pricing that seats 12. Disabled trips are not on the site.
  const renderCardPrice = (t) => {
    if (!cardPrices) return <span className="trip-admin-meta">-</span>;
    if (!(t.id in cardPrices)) return <span className="trip-admin-meta">Not on site</span>;
    const shown = Number(cardPrices[t.id]);
    const typed = Number(t.price);
    if (!(shown > 0)) return <span className="trip-admin-meta">Price on request</span>;
    return (
      <>
        <div className="fw-semibold text-nowrap">{formatRupees(shown)}</div>
        {typed > 0 && typed !== shown && (
          <div className="trip-admin-meta">Typed {formatRupees(typed)}</div>
        )}
      </>
    );
  };

  const renderTripRow = (t) => (
    <tr key={t.id} className={t.active === false ? "trip-admin-row-off" : undefined}>
      <td>
        {t.imageUrl ? (
          <img src={t.imageUrl} alt="" className="trip-admin-thumb" loading="lazy" />
        ) : (
          <div className="trip-admin-thumb trip-admin-thumb-empty" title="No card photo">
            <i className="bi bi-image" aria-hidden="true" />
          </div>
        )}
      </td>
      <td>
        <button type="button" className="trip-admin-title" onClick={() => openEditor(t)} title={t.title}>
          {t.title}
        </button>
        <div className="trip-admin-meta">
          {[t.duration, `/${t.path}`].filter(Boolean).join(" · ")}
        </div>
        {(!t.imageUrl || !t.coverUrl) && (
          <div className="d-flex gap-1 mt-1">
            {!t.imageUrl && <Badge bg="warning" text="dark">No card photo</Badge>}
            {!t.coverUrl && <Badge bg="warning" text="dark">No cover photo</Badge>}
          </div>
        )}
      </td>
      <td className="text-center">{t.displayOrder ?? "-"}</td>
      <td>{renderCardPrice(t)}</td>
      <td>
        <Badge bg={t.active === false ? "secondary" : "success"}>
          {t.active === false ? "Disabled" : "Live"}
        </Badge>
      </td>
      <td>
        <div className="trip-admin-actions">
          <Button size="sm" variant="primary" onClick={() => openEditor(t)}>
            <i className="bi bi-pencil-square me-1" aria-hidden="true" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            href={`/trips/${t.path}`}
            target="_blank"
            rel="noreferrer"
            title="Open the trip page"
            aria-label={`Open the ${t.title} trip page`}
          >
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setDuplicateSource(t)}>
            <i className="bi bi-files me-1" aria-hidden="true" />
            Duplicate
          </Button>
          <Button
            size="sm"
            variant={t.active === false ? "outline-success" : "outline-warning"}
            className="trip-admin-toggle"
            onClick={() => toggleTripActive(t)}
            disabled={activeToggleId === t.id}
          >
            {activeToggleId === t.id ? (
              <Spinner size="sm" />
            ) : t.active === false ? (
              "Enable"
            ) : (
              "Disable"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => onDelete(t)}
            title="Delete trip"
            aria-label={`Delete ${t.title}`}
          >
            <i className="bi bi-trash" aria-hidden="true" />
          </Button>
        </div>
      </td>
    </tr>
  );

  // One table per category so the columns line up; each state is a heading row inside it.
  const renderCategoryTable = (group) => (
    <div className="table-responsive">
      <Table hover className="mb-0 align-middle trip-admin-table">
        <thead className="table-light">
          <tr>
            <th style={{ width: 76 }}>Photo</th>
            <th>Trip</th>
            <th className="text-center" style={{ width: 70 }}>Order</th>
            <th
              style={{ width: 150 }}
              title="What the homepage card shows: per person for 12 people from the default pickup, or the typed price when the pricing cannot seat 12"
            >
              Card price
            </th>
            <th style={{ width: 96 }}>Status</th>
            <th className="text-end" style={{ width: 352 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {group.states.map((stateGroup) => (
            <React.Fragment key={stateGroup.key}>
              <tr className="trip-admin-state-row">
                <td colSpan={6}>
                  <span className="me-2">{stateGroup.name}</span>
                  <Badge
                    bg={stateGroup.name === "State not set" ? "warning" : "secondary"}
                    text={stateGroup.name === "State not set" ? "dark" : undefined}
                  >
                    {stateGroup.trips.length}
                  </Badge>
                </td>
              </tr>
              {stateGroup.trips.map(renderTripRow)}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );

  return (
    <>
      <Card className="shadow-sm mb-3">
        <Card.Header className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">Manage Trips</div>
            {!loading && (
              <div className="small text-muted">
                {rows.length} trips · {rows.filter((trip) => trip.active !== false).length} live
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <InputGroup size="sm">
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="title, category, state, path, departure"
              />
            </InputGroup>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setShowCategories(true)}
            >
              Manage Categories
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setShow(true);
              }}
            >
              + New
            </Button>
          </div>
        </Card.Header>

        {loading ? (
          <div className="py-5 text-center">
            <Spinner /> <div className="small text-muted mt-2">Loading…</div>
          </div>
        ) : groupedTrips.length ? (
          <Accordion
            alwaysOpen
            defaultActiveKey={groupedTrips.map((group) => group.key)}
            className="trip-category-accordion"
          >
            {groupedTrips.map((group) => (
              <Accordion.Item eventKey={group.key} key={group.key}>
                <Accordion.Header>
                  <span className="fw-semibold me-2">{group.name}</span>
                  <Badge bg="secondary">{group.tripCount}</Badge>
                  {disabledCategoryIds.has(group.key) && <Badge bg="dark" className="ms-2">Category disabled</Badge>}
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  {renderCategoryTable(group)}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : (
          <div className="text-center py-4 text-muted">No trips found.</div>
        )}
      </Card>

      <TripModal
        show={show}
        onHide={() => setShow(false)}
        initial={editing}
        categories={categories}
        onSave={async (v) => {
          await upsert(v);
          setShow(false);
          setEditing(null);
          loadCardPrices();
        }}
        onPatched={onPatched}
      />
      <DuplicateTripModal
        show={!!duplicateSource}
        source={duplicateSource}
        trips={rows}
        onHide={() => setDuplicateSource(null)}
        onDuplicate={duplicateTrip}
      />
      <CategoryManagerModal
        show={showCategories}
        categories={categories}
        tripCountByCategory={tripCountByCategory}
        onHide={() => setShowCategories(false)}
        onSaved={load}
      />
    </>
  );
}

function DuplicateTripModal({ show, source, trips = [], onHide, onDuplicate }) {
  const [form, setForm] = useState(() => duplicateDefaults(source, trips));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(duplicateDefaults(source, trips));
      setSaving(false);
    }
  }, [show, source, trips]);

  if (!source) return null;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const changeDuration = (value) => {
    const option = DUPLICATE_DURATION_OPTIONS.find((item) => `${item.nights}-${item.days}` === value);
    if (!option) return;
    setForm((current) => ({
      ...current,
      duration: option.label,
      durationNights: option.nights,
      durationDays: option.days,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return toast.error("New trip title is required");
    if (!form.path.trim()) return toast.error("New trip path is required");
    setSaving(true);
    try {
      await onDuplicate(source, {
        title: form.title.trim(),
        path: form.path.trim(),
        duration: form.duration.trim(),
        durationNights: Number(form.durationNights || 1),
        durationDays: Number(form.durationDays || 2),
        copyDynamicPricing: !!form.copyDynamicPricing,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data || "Could not duplicate trip");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Duplicate Trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            Source: <span className="fw-semibold">{source.title}</span>
          </div>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>New Trip Title</Form.Label>
              <Form.Control value={form.title} onChange={(e) => update("title", e.target.value)} disabled={saving} required />
            </Col>
            <Col xs={12}>
              <Form.Label>New Path</Form.Label>
              <InputGroup>
                <InputGroup.Text>/trips/</InputGroup.Text>
                <Form.Control value={form.path} onChange={(e) => update("path", slugifyPath(e.target.value))} disabled={saving} required />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Label>Duration</Form.Label>
              <Form.Select value={`${form.durationNights}-${form.durationDays}`} onChange={(e) => changeDuration(e.target.value)} disabled={saving}>
                {DUPLICATE_DURATION_OPTIONS.map((option) => (
                  <option key={`${option.nights}-${option.days}`} value={`${option.nights}-${option.days}`}>{option.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Duration Label</Form.Label>
              <Form.Control value={form.duration} onChange={(e) => update("duration", e.target.value)} disabled={saving} />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                label="Also duplicate Dynamic Pricing as disabled draft"
                checked={form.copyDynamicPricing}
                onChange={(e) => update("copyDynamicPricing", e.target.checked)}
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Pricing room nights will be adjusted to the selected duration before review.
              </Form.Text>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-2" />Duplicating</> : "Create Duplicate"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function duplicateDefaults(source, trips = []) {
  if (!source) {
    const fallback = DUPLICATE_DURATION_OPTIONS[1];
    return { title: "", path: "", duration: fallback.label, durationNights: fallback.nights, durationDays: fallback.days, copyDynamicPricing: true };
  }
  const parsed = parseDuration(source.duration) || DUPLICATE_DURATION_OPTIONS[1];
  const title = `${source.title || "Trip"} Copy`;
  const basePath = `${source.path || slugifyPath(source.title || "trip")}-copy`;
  return {
    title,
    path: uniquePath(basePath, trips, source.id),
    duration: source.duration || parsed.label,
    durationNights: parsed.nights,
    durationDays: parsed.days,
    copyDynamicPricing: true,
  };
}

function parseDuration(value) {
  const match = String(value || "").match(/(\d+)\s*Night.*?(\d+)\s*Day/i);
  if (!match) return null;
  return {
    label: `${match[1]} ${Number(match[1]) === 1 ? "Night" : "Nights"} & ${match[2]} Days`,
    nights: Number(match[1]),
    days: Number(match[2]),
  };
}

function slugifyPath(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function uniquePath(base, trips = [], sourceId = null) {
  const cleaned = slugifyPath(base) || "trip-copy";
  const existing = new Set(trips.filter((trip) => trip.id !== sourceId).map((trip) => String(trip.path || "").toLowerCase()));
  let candidate = cleaned;
  let suffix = 2;
  while (existing.has(candidate.toLowerCase())) {
    candidate = `${cleaned}-${suffix}`;
    suffix += 1;
  }
  return candidate;
}

function CategoryManagerModal({ show, categories, tripCountByCategory, onHide, onSaved }) {
  const blank = { id: null, name: "", slug: "", displayOrder: 0, iconUrl: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    if (show) setForm({ id: null, name: "", slug: "", displayOrder: 0, iconUrl: "" });
  }, [show]);

  const editCategory = (category) => setForm({
    id: category.id,
    name: category.name || "",
    slug: category.slug || "",
    displayOrder: category.displayOrder ?? 0,
    iconUrl: category.iconUrl || "",
  });

  const saveCategory = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        displayOrder: Number(form.displayOrder || 0),
        iconUrl: form.iconUrl.trim() || null,
      };
      if (form.id) await api.put(`/api/admin/categories/${form.id}`, payload);
      else await api.post("/api/admin/categories", payload);
      toast.success(form.id ? "Category updated" : "Category added");
      setForm(blank);
      await onSaved();
    } catch (error) {
      const message = error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null);
      toast.error(message || "Could not save category");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryActive = async (category) => {
    const nextActive = category.active === false;
    if (!nextActive && !window.confirm(`Disable category "${category.name}"? It and all its trips will be hidden from the website.`)) return;
    setTogglingId(category.id);
    try {
      await api.patch(`/api/admin/categories/${category.id}/active`, { active: nextActive });
      toast.success(nextActive ? "Category enabled" : "Category disabled");
      await onSaved();
    } catch (error) {
      const message = error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null);
      toast.error(message || "Could not update category status");
    } finally {
      setTogglingId(null);
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) return;
    try {
      await api.delete(`/api/admin/categories/${category.id}`);
      if (form.id === category.id) setForm(blank);
      toast.success("Category deleted");
      await onSaved();
    } catch (error) {
      const message = error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null);
      toast.error(message || "Could not delete category");
    }
  };

  return <Modal show={show} onHide={onHide} size="lg" centered><Modal.Header closeButton><Modal.Title>Manage Trip Categories</Modal.Title></Modal.Header><Modal.Body><Form onSubmit={saveCategory} className="border rounded p-3 mb-3"><div className="fw-semibold mb-2">{form.id ? "Modify category" : "Add category"}</div><Row className="g-2"><Col md={6}><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} required disabled={saving} /></Col><Col md={6}><Form.Label>Slug</Form.Label><Form.Control value={form.slug} onChange={(e) => setForm((current) => ({ ...current, slug: e.target.value }))} placeholder="Generated from name when blank" disabled={saving} /></Col><Col md={3}><Form.Label>Display order</Form.Label><Form.Control type="number" min="0" value={form.displayOrder} onChange={(e) => setForm((current) => ({ ...current, displayOrder: e.target.value }))} disabled={saving} /></Col><Col md={9}><Form.Label>Icon URL</Form.Label><Form.Control value={form.iconUrl} onChange={(e) => setForm((current) => ({ ...current, iconUrl: e.target.value }))} placeholder="Optional" disabled={saving} /></Col></Row><div className="d-flex justify-content-end gap-2 mt-3">{form.id && <Button type="button" variant="outline-secondary" onClick={() => setForm(blank)} disabled={saving}>Cancel edit</Button>}<Button type="submit" disabled={saving || !form.name.trim()}>{saving ? <><Spinner size="sm" className="me-2" />Saving</> : form.id ? "Save Changes" : "Add Category"}</Button></div></Form><div className="table-responsive"><Table hover size="sm" className="align-middle mb-0"><thead><tr><th style={{ width: 44 }}>#</th><th>Order</th><th>Name</th><th>Slug</th><th>Icon</th><th>Trips</th><th>Status</th><th className="text-end">Actions</th></tr></thead><tbody>{[...categories].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || (a.id ?? 0) - (b.id ?? 0)).map((category, index) => { const tripCount = tripCountByCategory[String(category.id)] || 0; return <tr key={category.id}><td className="small fw-semibold">{index + 1}</td><td>{category.displayOrder ?? 0}</td><td className="fw-semibold">{category.name}</td><td><code>{category.slug || "-"}</code></td><td>{category.iconUrl ? <img src={category.iconUrl} alt="" width="32" height="32" className="rounded" style={{ objectFit: "cover" }} /> : "-"}</td><td>{tripCount}</td><td><Badge bg={category.active === false ? "secondary" : "success"}>{category.active === false ? "Disabled" : "Live"}</Badge></td><td className="text-end"><div className="d-flex justify-content-end gap-2"><Button size="sm" variant="outline-secondary" onClick={() => editCategory(category)}>Edit</Button><Button size="sm" variant={category.active === false ? "outline-success" : "outline-warning"} onClick={() => toggleCategoryActive(category)} disabled={togglingId === category.id}>{togglingId === category.id ? <Spinner size="sm" /> : category.active === false ? "Enable" : "Disable"}</Button>{tripCount === 0 && <Button size="sm" variant="outline-danger" onClick={() => deleteCategory(category)}>Delete</Button>}</div></td></tr>; })}</tbody></Table></div></Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer></Modal>;
}

/* ---------------- Modal ---------------- */
function TripModal({ show, onHide, initial, categories, onSave, onPatched }) {
  const [saving, setSaving] = useState(false);
  const [uploadingVideoKey, setUploadingVideoKey] = useState("");

  // list editor quick inputs
  const [newInclude, setNewInclude] = useState("");
  const [newExclude, setNewExclude] = useState("");

  const empty = {
    id: undefined,
    title: "",
    imageUrl: "",
    price: "",
    originalPrice: "",
    duration: "",
    displayOrder: "",
    active: true,
    state: "",
    path: "",
    categoryId: "",
    coverUrl: "",
    availability: "",
    overviewText: "",
    departureLocation: "",
    departureTime: "",
    priceIncludes: [],
    priceExcludes: [],
    itineraryDays: [], // UI always uses array of days
    dayWisePlanDays: [],
    galleryImages: [],
    faqsList: [], // [{question, answer}]
    highlightsList: [],
    termsList: [],
    pickupOptions: [],
  };

  // utils
  const ensureArray = (v) => (Array.isArray(v) ? v : []);
  const extractItineraryAsArray = (it) => {
    if (!it) return [];
    if (Array.isArray(it)) return it; // legacy array
    if (Array.isArray(it.days)) return it.days; // { days: [...] }
    return [];
  };
  const extractFaqs = (f) => (Array.isArray(f) ? f : []);
  const extractHighlights = (h) => (Array.isArray(h) ? h : []);
  const cancellationPolicyPoint =
    "As Indian Odyssey Trips collects only a minimal advance payment to block hotel rooms, vehicles, and travel arrangements, cancellation is not available after booking confirmation.";
  const isCancellationPolicyTitle = (title) =>
    String(title || "").trim().toLowerCase().includes("cancellation");
  const extractTerms = (t) => {
    const terms = Array.isArray(t) ? t : [];
    let foundCancellationPolicy = false;
    const normalized = terms.map((term) => {
      if (!isCancellationPolicyTitle(term?.title)) return term;
      foundCancellationPolicy = true;
      return {
        ...term,
        title: "Cancellation Policy",
        points: [cancellationPolicyPoint],
      };
    });
    return foundCancellationPolicy
      ? normalized
      : [...normalized, { title: "Cancellation Policy", points: [cancellationPolicyPoint] }];
  };
  const normalizePickupOptions = (options) =>
    ensureArray(options).map((option, idx) => ({
      id: option.id,
      pickupLabel: option.pickupLabel || "",
      pickupValue: option.pickupValue || "",
      displayOrder: option.displayOrder ?? idx,
      isDefault: Boolean(option.isDefault),
      isActive: option.isActive !== false,
      overviewText: option.overviewText || "",
      departureLocation: option.departureLocation || "",
      returnLocation: option.returnLocation || "",
      departureTime: option.departureTime || "",
      priceIncludes: ensureArray(option.priceIncludes),
      priceExcludes: ensureArray(option.priceExcludes),
      videos: ensureArray(option.videos).map((video) => ({
        language: video.language || DEFAULT_VIDEO_LANGUAGE,
        title: video.title || "",
        youtubeUrl: video.youtubeUrl || "",
        downloadUrl: video.downloadUrl || "",
      })),
      itinerary: extractItineraryAsArray(option.itinerary),
      dayWisePlan: extractItineraryAsArray(option.dayWisePlan).map((day) => ({
        day: day.day || "",
        title: day.title || "",
        locations: ensureArray(day.locations).map((location) => ({
          locationName: location.locationName || location.name || "",
          googleMapLink: location.googleMapLink || location.mapLink || "",
          arrivalRadiusM: location.arrivalRadiusM ?? "",
        })),
      })),
    }));

  const normalize = (t) => {
    if (!t) return { ...empty };
    return {
      ...empty,
      ...t,
      active: t.active !== false,
      categoryId: t.categoryId ?? "",
      priceIncludes: ensureArray(t.priceIncludes),
      priceExcludes: ensureArray(t.priceExcludes),
      itineraryDays: extractItineraryAsArray(t.itinerary),
      dayWisePlanDays: extractItineraryAsArray(t.dayWisePlan).map((day) => ({
        day: day.day || "",
        title: day.title || "",
        locations: ensureArray(day.locations).map((location) => ({
          locationName: location.locationName || location.name || "",
          googleMapLink: location.googleMapLink || location.mapLink || "",
          arrivalRadiusM: location.arrivalRadiusM ?? "",
        })),
      })),
      faqsList: extractFaqs(t.faqs),
      highlightsList: extractHighlights(t.highlights),
      termsList: extractTerms(t.termsConditions),
      galleryImages: ensureArray(t.galleryImages),
      pickupOptions: normalizePickupOptions(t.pickupOptions),
    };
  };

  const [form, setForm] = useState(normalize(initial));
  const [activeTab, setActiveTab] = useState("basics");
  const invalidHandled = useRef(false);

  useEffect(() => {
    if (show) {
      setForm(normalize(initial));
      setNewInclude("");
      setNewExclude("");
      setSaving(false);
      setActiveTab("basics");
    }
  }, [initial, show]);

  // A field that fails the browser's checks may sit on a tab that is not showing, and the browser
  // cannot point at a hidden field, so Save would silently do nothing. Open that tab and point there.
  const showInvalidField = (event) => {
    if (invalidHandled.current) return;
    invalidHandled.current = true;
    setTimeout(() => {
      invalidHandled.current = false;
    }, 0);
    const field = event.target;
    const tab = field.closest?.("[data-tab]")?.dataset.tab;
    if (!tab || tab === activeTab) return;
    setActiveTab(tab);
    setTimeout(() => field.reportValidity?.(), 50);
  };

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ---------- List editor helpers (Includes/Excludes) ----------
  const addListItem = (key, value) => {
    const v = (value || "").trim();
    if (!v) return;
    setForm((p) => ({ ...p, [key]: [...(p[key] || []), v] }));
  };
  const updateListItem = (key, idx, value) => {
    setForm((p) => {
      const arr = [...(p[key] || [])];
      arr[idx] = value;
      return { ...p, [key]: arr };
    });
  };
  const removeListItem = (key, idx) => {
    setForm((p) => {
      const arr = [...(p[key] || [])];
      arr.splice(idx, 1);
      return { ...p, [key]: arr };
    });
  };
  const moveItem = (key, idx, dir) => {
    setForm((p) => {
      const arr = [...(p[key] || [])];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return p;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...p, [key]: arr };
    });
  };

  // ---------- Itinerary editor helpers ----------
  const addDay = () => {
    const dayNumber = (form.itineraryDays?.length || 0) + 1;
    const newDay = {
      day: `Day ${dayNumber}`,
      title: "",
      points: [],
      stayInfo: "",
    };
    update("itineraryDays", [...(form.itineraryDays || []), newDay]);
  };
  const updateDayField = (idx, field, value) => {
    const days = [...(form.itineraryDays || [])];
    days[idx] = { ...days[idx], [field]: value };
    update("itineraryDays", days);
  };
  const removeDay = (idx) => {
    const days = [...(form.itineraryDays || [])];
    days.splice(idx, 1);
    update("itineraryDays", days);
  };
  const moveDay = (idx, dir) => {
    const days = [...(form.itineraryDays || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= days.length) return;
    [days[idx], days[newIdx]] = [days[newIdx], days[idx]];
    update("itineraryDays", days);
  };

  // points inside a day
  const addPoint = (dayIdx, text) => {
    const v = (text || "").trim();
    if (!v) return;
    const days = [...(form.itineraryDays || [])];
    const pts = [...(days[dayIdx].points || []), v];
    days[dayIdx] = { ...days[dayIdx], points: pts };
    update("itineraryDays", days);
  };
  const updatePoint = (dayIdx, pointIdx, value) => {
    const days = [...(form.itineraryDays || [])];
    const pts = [...(days[dayIdx].points || [])];
    pts[pointIdx] = value;
    days[dayIdx] = { ...days[dayIdx], points: pts };
    update("itineraryDays", days);
  };
  const removePoint = (dayIdx, pointIdx) => {
    const days = [...(form.itineraryDays || [])];
    const pts = [...(days[dayIdx].points || [])];
    pts.splice(pointIdx, 1);
    days[dayIdx] = { ...days[dayIdx], points: pts };
    update("itineraryDays", days);
  };
  const movePoint = (dayIdx, pointIdx, dir) => {
    const days = [...(form.itineraryDays || [])];
    const pts = [...(days[dayIdx].points || [])];
    const newIdx = pointIdx + dir;
    if (newIdx < 0 || newIdx >= pts.length) return;
    [pts[pointIdx], pts[newIdx]] = [pts[newIdx], pts[pointIdx]];
    days[dayIdx] = { ...days[dayIdx], points: pts };
    update("itineraryDays", days);
  };

  // ---------- Day-wise driver plan helpers ----------
  const addPlanDay = () => {
    const dayNumber = (form.dayWisePlanDays?.length || 0) + 1;
    update("dayWisePlanDays", [
      ...(form.dayWisePlanDays || []),
      { day: `Day ${dayNumber}`, title: "", locations: [] },
    ]);
  };
  const updatePlanDayField = (idx, field, value) => {
    const days = [...(form.dayWisePlanDays || [])];
    days[idx] = { ...days[idx], [field]: value };
    update("dayWisePlanDays", days);
  };
  const removePlanDay = (idx) => {
    const days = [...(form.dayWisePlanDays || [])];
    days.splice(idx, 1);
    update("dayWisePlanDays", days);
  };
  const movePlanDay = (idx, dir) => {
    const days = [...(form.dayWisePlanDays || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= days.length) return;
    [days[idx], days[newIdx]] = [days[newIdx], days[idx]];
    update("dayWisePlanDays", days);
  };
  const addPlanLocation = (dayIdx) => {
    const days = [...(form.dayWisePlanDays || [])];
    const locations = [...(days[dayIdx].locations || []), { locationName: "", googleMapLink: "" }];
    days[dayIdx] = { ...days[dayIdx], locations };
    update("dayWisePlanDays", days);
  };
  const updatePlanLocation = (dayIdx, locationIdx, field, value) => {
    const days = [...(form.dayWisePlanDays || [])];
    const locations = [...(days[dayIdx].locations || [])];
    locations[locationIdx] = { ...locations[locationIdx], [field]: value };
    days[dayIdx] = { ...days[dayIdx], locations };
    update("dayWisePlanDays", days);
  };
  const removePlanLocation = (dayIdx, locationIdx) => {
    const days = [...(form.dayWisePlanDays || [])];
    const locations = [...(days[dayIdx].locations || [])];
    locations.splice(locationIdx, 1);
    days[dayIdx] = { ...days[dayIdx], locations };
    update("dayWisePlanDays", days);
  };
  const movePlanLocation = (dayIdx, locationIdx, dir) => {
    const days = [...(form.dayWisePlanDays || [])];
    const locations = [...(days[dayIdx].locations || [])];
    const newIdx = locationIdx + dir;
    if (newIdx < 0 || newIdx >= locations.length) return;
    [locations[locationIdx], locations[newIdx]] = [locations[newIdx], locations[locationIdx]];
    days[dayIdx] = { ...days[dayIdx], locations };
    update("dayWisePlanDays", days);
  };

  // ---------- FAQs editor helpers ----------
  const addFaq = () => {
    update("faqsList", [
      ...(form.faqsList || []),
      { question: "", answer: "" },
    ]);
  };
  const updateFaq = (idx, field, value) => {
    const faqs = [...(form.faqsList || [])];
    faqs[idx] = { ...faqs[idx], [field]: value };
    update("faqsList", faqs);
  };
  const removeFaq = (idx) => {
    const faqs = [...(form.faqsList || [])];
    faqs.splice(idx, 1);
    update("faqsList", faqs);
  };
  const moveFaq = (idx, dir) => {
    const faqs = [...(form.faqsList || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= faqs.length) return;
    [faqs[idx], faqs[newIdx]] = [faqs[newIdx], faqs[idx]];
    update("faqsList", faqs);
  };

  const addHighlight = () => {
    update("highlightsList", [
      ...(form.highlightsList || []),
      { icon: "", title: "", description: "" },
    ]);
  };
  const updateHighlight = (idx, field, value) => {
    const items = [...(form.highlightsList || [])];
    items[idx] = { ...items[idx], [field]: value };
    update("highlightsList", items);
  };
  const removeHighlight = (idx) => {
    const items = [...(form.highlightsList || [])];
    items.splice(idx, 1);
    update("highlightsList", items);
  };
  const moveHighlight = (idx, dir) => {
    const items = [...(form.highlightsList || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    update("highlightsList", items);
  };

  const addTerm = () => {
    update("termsList", [
      ...(form.termsList || []),
      { title: "", points: [] },
    ]);
  };
  const updateTerm = (idx, field, value) => {
    const items = [...(form.termsList || [])];
    items[idx] = { ...items[idx], [field]: value };
    update("termsList", items);
  };
  const removeTerm = (idx) => {
    const items = [...(form.termsList || [])];
    items.splice(idx, 1);
    update("termsList", items);
  };
  const moveTerm = (idx, dir) => {
    const items = [...(form.termsList || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    update("termsList", items);
  };
  const addTermPoint = (termIdx, value) => {
    const v = (value || "").trim();
    if (!v) return;
    const items = [...(form.termsList || [])];
    const term = { ...(items[termIdx] || {}) };
    term.points = [...(term.points || []), v];
    items[termIdx] = term;
    update("termsList", items);
  };
  const updateTermPoint = (termIdx, pointIdx, value) => {
    const items = [...(form.termsList || [])];
    const term = { ...(items[termIdx] || {}) };
    const points = [...(term.points || [])];
    points[pointIdx] = value;
    term.points = points;
    items[termIdx] = term;
    update("termsList", items);
  };
  const removeTermPoint = (termIdx, pointIdx) => {
    const items = [...(form.termsList || [])];
    const term = { ...(items[termIdx] || {}) };
    const points = [...(term.points || [])];
    points.splice(pointIdx, 1);
    term.points = points;
    items[termIdx] = term;
    update("termsList", items);
  };
  const moveTermPoint = (termIdx, pointIdx, dir) => {
    const items = [...(form.termsList || [])];
    const term = { ...(items[termIdx] || {}) };
    const points = [...(term.points || [])];
    const newIdx = pointIdx + dir;
    if (newIdx < 0 || newIdx >= points.length) return;
    [points[pointIdx], points[newIdx]] = [points[newIdx], points[pointIdx]];
    term.points = points;
    items[termIdx] = term;
    update("termsList", items);
  };

  // ---------- Upload helpers ----------
  const slugify = (s) =>
    (s || "")
      .toString()
      .trim()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "") // accents
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-");

  const updatePickupOption = (idx, field, value) => {
    setForm((p) => {
      const options = [...(p.pickupOptions || [])];
      options[idx] = { ...options[idx], [field]: value };
      return { ...p, pickupOptions: options };
    });
  };

  const addPickupOption = () => {
    const nextIndex = form.pickupOptions?.length || 0;
    setForm((p) => ({
      ...p,
      pickupOptions: [
        ...(p.pickupOptions || []),
        {
          pickupLabel: "",
          pickupValue: "",
          displayOrder: nextIndex,
          isDefault: nextIndex === 0,
          isActive: true,
          overviewText: p.overviewText || "",
          departureLocation: p.departureLocation || "",
          returnLocation: "",
          departureTime: p.departureTime || "",
          priceIncludes: [...(p.priceIncludes || [])],
          priceExcludes: [...(p.priceExcludes || [])],
          itinerary: [...(p.itineraryDays || [])],
          dayWisePlan: [...(p.dayWisePlanDays || [])],
          videos: [],
        },
      ],
    }));
  };

  const addPickupVideo = (pickupIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const videos = [...(option.videos || [])];
      const used = new Set(videos.map((video) => video.language));
      const nextLanguage = VIDEO_LANGUAGES.find((lang) => !used.has(lang.code));
      if (!nextLanguage) return option;
      videos.push({
        language: nextLanguage.code,
        title: "",
        youtubeUrl: "",
        downloadUrl: "",
      });
      return { ...option, videos };
    });
  };

  const updatePickupVideo = (pickupIdx, videoIdx, field, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const videos = [...(option.videos || [])];
      videos[videoIdx] = { ...(videos[videoIdx] || {}), [field]: value };
      return { ...option, videos };
    });
  };

  const removePickupVideo = (pickupIdx, videoIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const videos = [...(option.videos || [])];
      videos.splice(videoIdx, 1);
      return { ...option, videos };
    });
  };

  // Uploads the downloadable MP4 straight to S3 and stores the returned CDN URL.
  const uploadPickupVideoFile = async (pickupIdx, videoIdx, file) => {
    const catId = form.categoryId;
    const tripPath = (form.path || "").trim();
    if (!tripPath) return alert("Set the Trip Path first.");
    if (!catId) return alert("Choose a Category first.");

    const option = form.pickupOptions?.[pickupIdx] || {};
    const video = option.videos?.[videoIdx] || {};
    const pickupSlug =
      option.pickupValue?.trim() || slugify(option.pickupLabel || "") || `pickup-${pickupIdx + 1}`;
    const language = video.language || DEFAULT_VIDEO_LANGUAGE;
    const ext = mimeToExtUpload(file.type);
    if (!["mp4", "webm", "mov"].includes(ext)) {
      return alert("Video must be an MP4, WebM or MOV file.");
    }

    try {
      setUploadingVideoKey(`${pickupIdx}-${videoIdx}`);
      const fileName = `video-${pickupSlug}-${language}.${ext}`;
      const publicUrl = await presignAndUpload({
        file,
        location: `${catId}/${tripPath}`,
        targetName: fileName,
        // Objects serve from the bucket origin, so the download attribute alone is
        // ignored cross-origin -- the object itself has to say "attachment".
        contentDisposition: `attachment; filename="${fileName}"`,
      });
      updatePickupVideo(pickupIdx, videoIdx, "downloadUrl", publicUrl);
      toast.success("Video uploaded. Click Save to store it.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Video upload failed");
    } finally {
      setUploadingVideoKey("");
    }
  };

  const removePickupOption = (idx) => {
    setForm((p) => {
      const options = [...(p.pickupOptions || [])];
      options.splice(idx, 1);
      return { ...p, pickupOptions: options };
    });
  };

  const setDefaultPickupOption = (idx) => {
    setForm((p) => ({
      ...p,
      pickupOptions: (p.pickupOptions || []).map((option, optionIdx) => ({
        ...option,
        isDefault: optionIdx === idx,
      })),
    }));
  };

  const updatePickupNested = (pickupIdx, updater) => {
    setForm((p) => {
      const options = [...(p.pickupOptions || [])];
      options[pickupIdx] = updater({ ...(options[pickupIdx] || {}) });
      return { ...p, pickupOptions: options };
    });
  };

  const addPickupListItem = (pickupIdx, key, value) => {
    const v = (value || "").trim();
    if (!v) return;
    updatePickupNested(pickupIdx, (option) => ({
      ...option,
      [key]: [...(option[key] || []), v],
    }));
  };

  const updatePickupListItem = (pickupIdx, key, itemIdx, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const arr = [...(option[key] || [])];
      arr[itemIdx] = value;
      return { ...option, [key]: arr };
    });
  };

  const removePickupListItem = (pickupIdx, key, itemIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const arr = [...(option[key] || [])];
      arr.splice(itemIdx, 1);
      return { ...option, [key]: arr };
    });
  };

  const movePickupListItem = (pickupIdx, key, itemIdx, dir) => {
    updatePickupNested(pickupIdx, (option) => {
      const arr = [...(option[key] || [])];
      const newIdx = itemIdx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return option;
      [arr[itemIdx], arr[newIdx]] = [arr[newIdx], arr[itemIdx]];
      return { ...option, [key]: arr };
    });
  };

  const addPickupDay = (pickupIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const dayNumber = itinerary.length + 1;
      itinerary.push({
        day: `Day ${dayNumber}`,
        title: "",
        points: [],
        stayInfo: "",
      });
      return { ...option, itinerary };
    });
  };

  const updatePickupDayField = (pickupIdx, dayIdx, field, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      itinerary[dayIdx] = { ...itinerary[dayIdx], [field]: value };
      return { ...option, itinerary };
    });
  };

  const removePickupDay = (pickupIdx, dayIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      itinerary.splice(dayIdx, 1);
      return { ...option, itinerary };
    });
  };

  const movePickupDay = (pickupIdx, dayIdx, dir) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const newIdx = dayIdx + dir;
      if (newIdx < 0 || newIdx >= itinerary.length) return option;
      [itinerary[dayIdx], itinerary[newIdx]] = [
        itinerary[newIdx],
        itinerary[dayIdx],
      ];
      return { ...option, itinerary };
    });
  };

  const addPickupPoint = (pickupIdx, dayIdx, text) => {
    const v = (text || "").trim();
    if (!v) return;
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const day = { ...(itinerary[dayIdx] || {}) };
      day.points = [...(day.points || []), v];
      itinerary[dayIdx] = day;
      return { ...option, itinerary };
    });
  };

  const updatePickupPoint = (pickupIdx, dayIdx, pointIdx, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const day = { ...(itinerary[dayIdx] || {}) };
      const points = [...(day.points || [])];
      points[pointIdx] = value;
      day.points = points;
      itinerary[dayIdx] = day;
      return { ...option, itinerary };
    });
  };

  const removePickupPoint = (pickupIdx, dayIdx, pointIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const day = { ...(itinerary[dayIdx] || {}) };
      const points = [...(day.points || [])];
      points.splice(pointIdx, 1);
      day.points = points;
      itinerary[dayIdx] = day;
      return { ...option, itinerary };
    });
  };

  const movePickupPoint = (pickupIdx, dayIdx, pointIdx, dir) => {
    updatePickupNested(pickupIdx, (option) => {
      const itinerary = [...(option.itinerary || [])];
      const day = { ...(itinerary[dayIdx] || {}) };
      const points = [...(day.points || [])];
      const newIdx = pointIdx + dir;
      if (newIdx < 0 || newIdx >= points.length) return option;
      [points[pointIdx], points[newIdx]] = [points[newIdx], points[pointIdx]];
      day.points = points;
      itinerary[dayIdx] = day;
      return { ...option, itinerary };
    });
  };

  const addPickupPlanDay = (pickupIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      dayWisePlan.push({
        day: `Day ${dayWisePlan.length + 1}`,
        title: "",
        locations: [],
      });
      return { ...option, dayWisePlan };
    });
  };

  const copyDefaultPlanToPickup = (pickupIdx) => {
    updatePickupNested(pickupIdx, (option) => ({
      ...option,
      dayWisePlan: (form.dayWisePlanDays || []).map((day) => ({
        day: day.day || "",
        title: day.title || "",
        locations: (day.locations || []).map((location) => ({
          locationName: location.locationName || "",
          googleMapLink: location.googleMapLink || "",
          arrivalRadiusM: location.arrivalRadiusM ?? "",
        })),
      })),
    }));
  };

  const updatePickupPlanDayField = (pickupIdx, dayIdx, field, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      dayWisePlan[dayIdx] = { ...dayWisePlan[dayIdx], [field]: value };
      return { ...option, dayWisePlan };
    });
  };

  const removePickupPlanDay = (pickupIdx, dayIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      dayWisePlan.splice(dayIdx, 1);
      return { ...option, dayWisePlan };
    });
  };

  const movePickupPlanDay = (pickupIdx, dayIdx, dir) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      const newIdx = dayIdx + dir;
      if (newIdx < 0 || newIdx >= dayWisePlan.length) return option;
      [dayWisePlan[dayIdx], dayWisePlan[newIdx]] = [dayWisePlan[newIdx], dayWisePlan[dayIdx]];
      return { ...option, dayWisePlan };
    });
  };

  const addPickupPlanLocation = (pickupIdx, dayIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      const day = { ...(dayWisePlan[dayIdx] || {}) };
      day.locations = [...(day.locations || []), { locationName: "", googleMapLink: "" }];
      dayWisePlan[dayIdx] = day;
      return { ...option, dayWisePlan };
    });
  };

  const updatePickupPlanLocation = (pickupIdx, dayIdx, locationIdx, field, value) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      const day = { ...(dayWisePlan[dayIdx] || {}) };
      const locations = [...(day.locations || [])];
      locations[locationIdx] = { ...(locations[locationIdx] || {}), [field]: value };
      day.locations = locations;
      dayWisePlan[dayIdx] = day;
      return { ...option, dayWisePlan };
    });
  };

  const removePickupPlanLocation = (pickupIdx, dayIdx, locationIdx) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      const day = { ...(dayWisePlan[dayIdx] || {}) };
      const locations = [...(day.locations || [])];
      locations.splice(locationIdx, 1);
      day.locations = locations;
      dayWisePlan[dayIdx] = day;
      return { ...option, dayWisePlan };
    });
  };

  const movePickupPlanLocation = (pickupIdx, dayIdx, locationIdx, dir) => {
    updatePickupNested(pickupIdx, (option) => {
      const dayWisePlan = [...(option.dayWisePlan || [])];
      const day = { ...(dayWisePlan[dayIdx] || {}) };
      const locations = [...(day.locations || [])];
      const newIdx = locationIdx + dir;
      if (newIdx < 0 || newIdx >= locations.length) return option;
      [locations[locationIdx], locations[newIdx]] = [locations[newIdx], locations[locationIdx]];
      day.locations = locations;
      dayWisePlan[dayIdx] = day;
      return { ...option, dayWisePlan };
    });
  };

  const categoryFromId = (id) =>
    categories.find((c) => String(c.id) === String(id));
  const getCategorySlug = () => {
    const cat = categoryFromId(form.categoryId);
    return cat?.slug || cat?.urlSlug || slugify(cat?.name || "");
  };

  const mimeToExtUpload = (mime) => {
    if (!mime) return "bin";
    if (mime === "image/jpeg") return "jpg";
    if (mime === "image/png") return "png";
    if (mime === "image/webp") return "webp";
    if (mime === "image/gif") return "gif";
    if (mime === "video/mp4") return "mp4";
    if (mime === "video/webm") return "webm";
    if (mime === "video/quicktime") return "mov";
    return "bin";
  };

  const sanitizeFilename = (name, fallbackExt) => {
    let base = (name || `file.${fallbackExt || "bin"}`).split(/[/\\]/).pop();
    if (!base.includes(".")) base = `${base}.${fallbackExt || "bin"}`;
    const parts = base.split(".");
    const ext = parts.pop();
    let stem = parts.join(".");
    stem = stem
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9\-._]/g, "");
    if (stem.length > 60) stem = stem.slice(0, 60);
    if (!stem) stem = "file";
    return `${stem}.${ext.toLowerCase()}`;
  };

  const presignAndUpload = async ({
    file,
    location,
    targetName,
    contentDisposition,
  }) => {
    const contentType = file.type || "application/octet-stream";
    const { data } = await api.post("/api/admin/assets/presign", {
      location, // becomes 'trips/<location>/<fileName>'
      fileName: targetName,
      contentType, // must match PUT header
      contentDisposition, // optional; must also match the PUT header when set
    });

    const putRes = await fetch(data.uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
        ...(contentDisposition
          ? { "Content-Disposition": contentDisposition }
          : {}),
      },
      body: file,
    });
    if (!putRes.ok) {
      const text = await putRes.text().catch(() => "");
      throw new Error(
        `S3 upload failed: ${putRes.status} ${putRes.statusText} ${text}`
      );
    }
    return data.publicUrl; // e.g. https://iotrips.in/trips/<location>/<fileName>
  };

  const persistFieldToDb = async (fieldKey, publicUrl) => {
    if (!form.id) {
      update(fieldKey, publicUrl);
      alert("Assigned. This is a new trip—click Save to store it in DB.");
      return;
    }
    try {
      const { data: patched } = await api.patch(`/api/admin/trips/${form.id}`, {
        [fieldKey]: publicUrl,
      });
      update(fieldKey, publicUrl);
      onPatched?.(patched || { id: form.id, [fieldKey]: publicUrl });
    } catch {
      const payload = buildFullPayloadWithField(fieldKey, publicUrl);
      const { data: updated } = await api.put(
        `/api/admin/trips/${form.id}`,
        payload
      );
      update(fieldKey, publicUrl);
      onPatched?.(updated || { id: form.id, [fieldKey]: publicUrl });
    }
  };

  const persistGalleryArrayToDb = async (newArr) => {
    if (!form.id) {
      update("galleryImages", newArr);
      alert(
        "Gallery updated. This is a new trip—click Save to store it in DB."
      );
      return;
    }
    try {
      const { data: patched } = await api.patch(`/api/admin/trips/${form.id}`, {
        galleryImages: newArr,
      });
      update("galleryImages", newArr);
      onPatched?.(patched || { id: form.id, galleryImages: newArr });
    } catch (err) {
      // Fallback to PUT with full payload
      const payload = buildFullPayload();
      payload.galleryImages = newArr;
      const { data: updated } = await api.put(
        `/api/admin/trips/${form.id}`,
        payload
      );
      update("galleryImages", newArr);
      onPatched?.(updated || { id: form.id, galleryImages: newArr });
    }
  };

  const openDownload = (url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const deleteFromS3 = async (url) => {
    if (!url) return;
    if (!window.confirm("Delete this object from S3?")) return;
    await api.post("/api/admin/assets/delete", { url });
    alert(
      "Deleted from S3. If needed, clear any DB fields that still reference this URL."
    );
  };

  // ---- Build payloads (ALWAYS save itinerary as ARRAY) ----
  const buildItineraryForSave = () => form.itineraryDays || [];
  const buildDayWisePlanForSave = () =>
    (form.dayWisePlanDays || [])
      .map((day, idx) => ({
        day: day.day || `Day ${idx + 1}`,
        title: day.title || "",
        locations: (day.locations || [])
          .filter((location) => location.locationName?.trim() || location.googleMapLink?.trim())
          .map((location) => withPlaceCircle({
            locationName: location.locationName?.trim() || "",
            googleMapLink: location.googleMapLink?.trim() || "",
          }, location.arrivalRadiusM)),
      }))
      .filter((day) => day.title || day.locations.length);
  const buildPickupDayWisePlanForSave = (days = []) =>
    (days || [])
      .map((day, idx) => ({
        day: day.day || `Day ${idx + 1}`,
        title: day.title || "",
        locations: (day.locations || [])
          .filter((location) => location.locationName?.trim() || location.googleMapLink?.trim())
          .map((location) => withPlaceCircle({
            locationName: location.locationName?.trim() || "",
            googleMapLink: location.googleMapLink?.trim() || "",
          }, location.arrivalRadiusM)),
      }))
      .filter((day) => day.title || day.locations.length);

  const buildFullPayload = () => ({
    id: form.id,
    title: form.title,
    imageUrl: form.imageUrl || null,
    price: form.price || null,
    originalPrice: form.originalPrice || null,
    duration: form.duration || null,
    displayOrder:
      form.displayOrder === "" || form.displayOrder == null
        ? null
        : Number(form.displayOrder),
    active: form.active !== false,
    state: form.state?.trim() || null,
    path: form.path,
    categoryId: form.categoryId ? Number(form.categoryId) : null,
    coverUrl: form.coverUrl || null,
    availability: form.availability || null,
    overviewText: form.overviewText || null,
    departureLocation: form.departureLocation || null,
    departureTime: form.departureTime || null,
    priceIncludes: form.priceIncludes || [],
    priceExcludes: form.priceExcludes || [],
    itinerary: buildItineraryForSave(), // array only
    dayWisePlan: buildDayWisePlanForSave(),
    galleryImages: form.galleryImages || [],
    faqs: form.faqsList || [],
    highlights: form.highlightsList || [],
    termsConditions: form.termsList || [],
    pickupOptions: (form.pickupOptions || [])
      .filter((option) => option.pickupLabel?.trim())
      .map((option, idx) => ({
        id: option.id,
        pickupLabel: option.pickupLabel.trim(),
        pickupValue:
          option.pickupValue?.trim() || slugify(option.pickupLabel || ""),
        displayOrder: option.displayOrder ?? idx,
        isDefault: Boolean(option.isDefault),
        isActive: option.isActive !== false,
        overviewText: option.overviewText || null,
        departureLocation: option.departureLocation || null,
        returnLocation: option.returnLocation || null,
        departureTime: option.departureTime || null,
        priceIncludes: option.priceIncludes || [],
        priceExcludes: option.priceExcludes || [],
        itinerary: option.itinerary || [],
        dayWisePlan: buildPickupDayWisePlanForSave(option.dayWisePlan || []),
        videos: (option.videos || [])
          .filter((video) => video.youtubeUrl?.trim())
          .map((video) => ({
            language: video.language || DEFAULT_VIDEO_LANGUAGE,
            title: video.title?.trim() || null,
            youtubeUrl: video.youtubeUrl.trim(),
            downloadUrl: video.downloadUrl?.trim() || null,
          })),
      })),
  });

  const buildFullPayloadWithField = (fieldKey, publicUrl) => {
    const base = buildFullPayload();
    if (fieldKey === "imageUrl") base.imageUrl = publicUrl;
    if (fieldKey === "coverUrl") base.coverUrl = publicUrl;
    return base;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert("Title is required");
    if (!form.path.trim()) return alert("Path (unique) is required");
    const pickupOptions = form.pickupOptions || [];
    if (pickupOptions.length && !pickupOptions.some((option) => option.isDefault)) {
      setActiveTab("pickups");
      return alert("Choose one default pickup location");
    }
    try {
      setSaving(true);
      await onSave(buildFullPayload());
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save. Please try again.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      size="xl"
      centered
      scrollable
      className="trip-edit-modal"
    >
      <Form onSubmit={submit} onInvalidCapture={showInvalidField}>
        <Tab.Container activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)} transition={false}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {form.id ? "Edit Trip" : "New Trip"}
            {form.id && form.title ? <div className="trip-edit-subtitle">{form.title}</div> : null}
          </Modal.Title>
        </Modal.Header>
        <div className="trip-edit-tabs">
          <Nav variant="tabs">
            {TRIP_EDIT_TABS.map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link eventKey={tab.key}>
                  <i className={`bi ${tab.icon} me-1`} aria-hidden="true" />
                  {tab.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>
        <Modal.Body>
          <Tab.Content>
            <Tab.Pane eventKey="basics" data-tab="basics">
            <section className="trip-edit-section vstack gap-3">
          <Row>
            <Col md={8} className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                required
                disabled={saving}
              />
            </Col>
            <Col md={4} className="mb-2">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={form.categoryId}
                onChange={(e) => update("categoryId", e.target.value)}
                disabled={saving}
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>State</Form.Label>
              <Form.Control
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
                placeholder="e.g., Karnataka"
                disabled={saving}
              />
              <Form.Text className="text-muted">Trips are grouped by this state inside the selected category.</Form.Text>
            </Col>
            <Col md={6} className="mb-2 d-flex align-items-end">
              <Form.Check
                type="switch"
                id="trip-active-switch"
                label={form.active === false ? "Disabled for customers" : "Live for customers"}
                checked={form.active !== false}
                onChange={(e) => update("active", e.target.checked)}
                disabled={saving}
              />
            </Col>
          </Row>

          <Row>
            <Col md={3} className="mb-2">
              <Form.Label>Price (₹)</Form.Label>
              <Form.Control
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                disabled={saving}
              />
            </Col>
            <Col md={3} className="mb-2">
              <Form.Label>Original Price (₹)</Form.Label>
              <Form.Control
                value={form.originalPrice}
                onChange={(e) => update("originalPrice", e.target.value)}
                disabled={saving}
              />
            </Col>
            <Col md={3} className="mb-2">
              <Form.Label>Duration</Form.Label>
              <Form.Control
                value={form.duration}
                onChange={(e) => update("duration", e.target.value)}
                placeholder="e.g., 3 Days & 2 Nights"
                disabled={saving}
              />
            </Col>
            <Col md={3} className="mb-2">
              <Form.Label>Order within state</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={form.displayOrder}
                onChange={(e) => update("displayOrder", e.target.value)}
                placeholder="0 appears first"
                disabled={saving}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Path (unique)</Form.Label>
              <Form.Control
                value={form.path}
                onChange={(e) => update("path", e.target.value)}
                placeholder="e.g., chikmagalur"
                required
                disabled={saving}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Availability (short label)</Form.Label>
              <Form.Control
                value={form.availability}
                onChange={(e) => update("availability", e.target.value)}
                placeholder="The tour is available."
                disabled={saving}
              />
            </Col>
          </Row>

            </section>
            </Tab.Pane>
            <Tab.Pane eventKey="overview" data-tab="overview">
            <section className="trip-edit-section">
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0">Highlights</Form.Label>
            <Button size="sm" onClick={addHighlight} disabled={saving}>
              + Add Highlight
            </Button>
          </div>
          {form.highlightsList?.length ? (
            <ListGroup className="mb-3">
              {form.highlightsList.map((item, idx) => (
                <ListGroup.Item key={`highlight-${idx}`}>
                  <Row className="g-2 align-items-center">
                    <Col md={2}>
                      <Form.Control
                        value={item.icon || ""}
                        onChange={(e) =>
                          updateHighlight(idx, "icon", e.target.value)
                        }
                        placeholder="Icon"
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        value={item.title || ""}
                        onChange={(e) =>
                          updateHighlight(idx, "title", e.target.value)
                        }
                        placeholder="Title"
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        value={item.description || ""}
                        onChange={(e) =>
                          updateHighlight(idx, "description", e.target.value)
                        }
                        placeholder="Description"
                        disabled={saving}
                      />
                    </Col>
                    <Col md={2} className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={idx === 0 || saving}
                        onClick={() => moveHighlight(idx, -1)}
                      >
                        <i className="bi bi-arrow-up" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={idx === form.highlightsList.length - 1 || saving}
                        onClick={() => moveHighlight(idx, 1)}
                      >
                        <i className="bi bi-arrow-down" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeHighlight(idx)}
                        disabled={saving}
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted small mb-3">No highlights added yet.</div>
          )}

            </section>
            <section className="trip-edit-section">
          {/* Overview */}
          <Form.Label>Overview</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={form.overviewText}
            onChange={(e) => update("overviewText", e.target.value)}
            disabled={saving}
          />

          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Departure & Return Location</Form.Label>
              <Form.Control
                value={form.departureLocation}
                onChange={(e) => update("departureLocation", e.target.value)}
                placeholder="e.g., Bangalore / Mysore"
                disabled={saving}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Departure Time</Form.Label>
              <Form.Control
                value={form.departureTime}
                onChange={(e) => update("departureTime", e.target.value)}
                placeholder="e.g., Any Day Morning"
                disabled={saving}
              />
            </Col>
          </Row>

            </section>
            <section className="trip-edit-section">
          {/* Lists: Includes / Excludes */}
          <Row>
            {/* Price Includes */}
            <Col md={6}>
              <Form.Label>Price Includes</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  placeholder="Add an include item…"
                  value={newInclude}
                  onChange={(e) => setNewInclude(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addListItem("priceIncludes", newInclude);
                      setNewInclude("");
                    }
                  }}
                  disabled={saving}
                />
                <Button
                  onClick={() => {
                    addListItem("priceIncludes", newInclude);
                    setNewInclude("");
                  }}
                  disabled={saving}
                >
                  + Add
                </Button>
              </InputGroup>
              {form.priceIncludes?.length ? (
                <ListGroup className="mb-3">
                  {form.priceIncludes.map((it, idx) => (
                    <ListGroup.Item
                      key={`inc-${idx}`}
                      className="d-flex align-items-center gap-2"
                    >
                      <Form.Control
                        value={it}
                        onChange={(e) =>
                          updateListItem("priceIncludes", idx, e.target.value)
                        }
                        disabled={saving}
                      />
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={idx === 0 || saving}
                          onClick={() => moveItem("priceIncludes", idx, -1)}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={
                            idx === form.priceIncludes.length - 1 || saving
                          }
                          onClick={() => moveItem("priceIncludes", idx, 1)}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeListItem("priceIncludes", idx)}
                          title="Delete"
                          disabled={saving}
                        >
                          ✕
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-muted small mb-3">No items added yet.</div>
              )}
            </Col>

            {/* Price Excludes */}
            <Col md={6}>
              <Form.Label>Price Excludes</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  placeholder="Add an exclude item…"
                  value={newExclude}
                  onChange={(e) => setNewExclude(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addListItem("priceExcludes", newExclude);
                      setNewExclude("");
                    }
                  }}
                  disabled={saving}
                />
                <Button
                  onClick={() => {
                    addListItem("priceExcludes", newExclude);
                    setNewExclude("");
                  }}
                  disabled={saving}
                >
                  + Add
                </Button>
              </InputGroup>
              {form.priceExcludes?.length ? (
                <ListGroup className="mb-3">
                  {form.priceExcludes.map((it, idx) => (
                    <ListGroup.Item
                      key={`exc-${idx}`}
                      className="d-flex align-items-center gap-2"
                    >
                      <Form.Control
                        value={it}
                        onChange={(e) =>
                          updateListItem("priceExcludes", idx, e.target.value)
                        }
                        disabled={saving}
                      />
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={idx === 0 || saving}
                          onClick={() => moveItem("priceExcludes", idx, -1)}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={
                            idx === form.priceExcludes.length - 1 || saving
                          }
                          onClick={() => moveItem("priceExcludes", idx, 1)}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeListItem("priceExcludes", idx)}
                          title="Delete"
                          disabled={saving}
                        >
                          ✕
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-muted small mb-3">No items added yet.</div>
              )}
            </Col>
          </Row>

            </section>
            </Tab.Pane>
            <Tab.Pane eventKey="itinerary" data-tab="itinerary">
            <section className="trip-edit-section">
          {/* Itinerary editor */}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <Form.Label className="mb-0">Itinerary</Form.Label>
            <Button size="sm" onClick={addDay} disabled={saving}>
              + Add Day
            </Button>
          </div>
          {form.itineraryDays?.length ? (
            <ListGroup className="mb-3">
              {form.itineraryDays.map((day, idx) => (
                <ListGroup.Item key={`day-${idx}`}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Control
                      style={{ maxWidth: 140 }}
                      value={day.day || ""}
                      onChange={(e) =>
                        updateDayField(idx, "day", e.target.value)
                      }
                      placeholder="Day 1"
                      disabled={saving}
                    />
                    <Form.Control
                      value={day.title || ""}
                      onChange={(e) =>
                        updateDayField(idx, "title", e.target.value)
                      }
                      placeholder="Title"
                      disabled={saving}
                    />
                    <div className="ms-auto d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={idx === 0 || saving}
                        onClick={() => moveDay(idx, -1)}
                        title="Move day up"
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={
                          idx === form.itineraryDays.length - 1 || saving
                        }
                        onClick={() => moveDay(idx, 1)}
                        title="Move day down"
                      >
                        ↓
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeDay(idx)}
                        title="Delete day"
                        disabled={saving}
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  <InputGroup className="mb-2">
                    <InputGroup.Text>Stay Info</InputGroup.Text>
                    <Form.Control
                      value={day.stayInfo || ""}
                      onChange={(e) =>
                        updateDayField(idx, "stayInfo", e.target.value)
                      }
                      placeholder="Optional stay details"
                      disabled={saving}
                    />
                  </InputGroup>

                  {/* Points list */}
                  <PointsEditor
                    points={day.points || []}
                    onAdd={(txt) => addPoint(idx, txt)}
                    onUpdate={(pi, val) => updatePoint(idx, pi, val)}
                    onRemove={(pi) => removePoint(idx, pi)}
                    onMove={(pi, dir) => movePoint(idx, pi, dir)}
                    disabled={saving}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted small mb-3">No days added yet.</div>
          )}

            </section>
            <section className="trip-edit-section">
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    <Form.Label className="mb-0">Day-Wise Plan</Form.Label>
                    <div className="small text-muted">
                      Internal driver tracking plan. This is not shown on the customer trip page.
                    </div>
                  </div>
                  <Button size="sm" onClick={addPlanDay} disabled={saving}>
                    + Add Day
                  </Button>
                </div>

                {form.dayWisePlanDays?.length ? (
                  <ListGroup className="mb-3 mt-2">
                    {form.dayWisePlanDays.map((day, idx) => (
                      <ListGroup.Item key={`plan-day-${idx}`}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Form.Control
                            style={{ maxWidth: 140 }}
                            value={day.day || ""}
                            onChange={(e) => updatePlanDayField(idx, "day", e.target.value)}
                            placeholder="Day 1"
                            disabled={saving}
                          />
                          <Form.Control
                            value={day.title || ""}
                            onChange={(e) => updatePlanDayField(idx, "title", e.target.value)}
                            placeholder="Day title / route"
                            disabled={saving}
                          />
                          <div className="ms-auto d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={idx === 0 || saving}
                              onClick={() => movePlanDay(idx, -1)}
                              title="Move day up"
                            >
                              ↑
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={idx === form.dayWisePlanDays.length - 1 || saving}
                              onClick={() => movePlanDay(idx, 1)}
                              title="Move day down"
                            >
                              ↓
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removePlanDay(idx)}
                              title="Delete day"
                              disabled={saving}
                            >
                              ✕
                            </Button>
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="small mb-0">Locations</Form.Label>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => addPlanLocation(idx)}
                            disabled={saving}
                          >
                            + Add Location
                          </Button>
                        </div>

                        {day.locations?.length ? (
                          <ListGroup>
                            {day.locations.map((location, locationIdx) => (
                              <ListGroup.Item
                                key={`plan-location-${idx}-${locationIdx}`}
                                className="d-flex align-items-center gap-2"
                              >
                                <Form.Control
                                  value={location.locationName || ""}
                                  onChange={(e) =>
                                    updatePlanLocation(idx, locationIdx, "locationName", e.target.value)
                                  }
                                  placeholder="Location name"
                                  disabled={saving}
                                />
                                <Form.Control
                                  value={location.googleMapLink || ""}
                                  onChange={(e) =>
                                    updatePlanLocation(idx, locationIdx, "googleMapLink", e.target.value)
                                  }
                                  placeholder="Google Map link"
                                  disabled={saving}
                                />
                                <Form.Control
                                  type="number"
                                  min={PLACE_CIRCLE_MIN}
                                  max={PLACE_CIRCLE_MAX}
                                  step={50}
                                  className="trip-plan-circle"
                                  value={location.arrivalRadiusM ?? ""}
                                  onChange={(e) =>
                                    updatePlanLocation(idx, locationIdx, "arrivalRadiusM", e.target.value)
                                  }
                                  isInvalid={placeCircleInvalid(location.arrivalRadiusM)}
                                  placeholder="Circle m"
                                  title={PLACE_CIRCLE_HINT}
                                  aria-label="Own arrival circle in metres"
                                  disabled={saving}
                                />
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={locationIdx === 0 || saving}
                                    onClick={() => movePlanLocation(idx, locationIdx, -1)}
                                    title="Move location up"
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={locationIdx === day.locations.length - 1 || saving}
                                    onClick={() => movePlanLocation(idx, locationIdx, 1)}
                                    title="Move location down"
                                  >
                                    ↓
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removePlanLocation(idx, locationIdx)}
                                    title="Delete location"
                                    disabled={saving}
                                  >
                                    ✕
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <div className="text-muted small">No locations added yet.</div>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-muted small mt-2 mb-3">
                    No day-wise driver plan added yet.
                  </div>
                )}
            </section>
            </Tab.Pane>
            <Tab.Pane eventKey="faqs" data-tab="faqs">
            <section className="trip-edit-section">
          {/* FAQs editor */}
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0">FAQs</Form.Label>
            <Button size="sm" onClick={addFaq} disabled={saving}>
              + Add FAQ
            </Button>
          </div>
          {form.faqsList?.length ? (
            <ListGroup className="mb-3">
              {form.faqsList.map((f, idx) => (
                <ListGroup.Item key={`faq-${idx}`}>
                  <Form.Control
                    className="mb-2"
                    placeholder="Question"
                    value={f.question || ""}
                    onChange={(e) => updateFaq(idx, "question", e.target.value)}
                    disabled={saving}
                  />
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Answer"
                    value={f.answer || ""}
                    onChange={(e) => updateFaq(idx, "answer", e.target.value)}
                    disabled={saving}
                  />
                  <div className="mt-2 d-flex gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={idx === 0 || saving}
                      onClick={() => moveFaq(idx, -1)}
                      title="Move FAQ up"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={idx === form.faqsList.length - 1 || saving}
                      onClick={() => moveFaq(idx, 1)}
                      title="Move FAQ down"
                    >
                      ↓
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeFaq(idx)}
                      title="Delete FAQ"
                      disabled={saving}
                    >
                      ✕
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted small mb-3">No FAQs added yet.</div>
          )}

            </section>
            <section className="trip-edit-section">
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0">Terms & Conditions</Form.Label>
            <Button size="sm" onClick={addTerm} disabled={saving}>
              + Add Section
            </Button>
          </div>
          {form.termsList?.length ? (
            <ListGroup className="mb-3">
              {form.termsList.map((term, idx) => (
                <ListGroup.Item key={`term-${idx}`}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Control
                      value={term.title || ""}
                      onChange={(e) => updateTerm(idx, "title", e.target.value)}
                      placeholder="Section title"
                      disabled={saving}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={idx === 0 || saving}
                      onClick={() => moveTerm(idx, -1)}
                    >
                      <i className="bi bi-arrow-up" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={idx === form.termsList.length - 1 || saving}
                      onClick={() => moveTerm(idx, 1)}
                    >
                      <i className="bi bi-arrow-down" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeTerm(idx)}
                      disabled={saving}
                    >
                      <i className="bi bi-x-lg" aria-hidden="true" />
                    </Button>
                  </div>
                  <PointsEditor
                    points={term.points || []}
                    onAdd={(txt) => addTermPoint(idx, txt)}
                    onUpdate={(pointIdx, value) =>
                      updateTermPoint(idx, pointIdx, value)
                    }
                    onRemove={(pointIdx) => removeTermPoint(idx, pointIdx)}
                    onMove={(pointIdx, dir) =>
                      moveTermPoint(idx, pointIdx, dir)
                    }
                    disabled={saving}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted small mb-3">
              No terms and conditions added yet.
            </div>
          )}

            </section>
            </Tab.Pane>
            <Tab.Pane eventKey="pickups" data-tab="pickups">
            <section className="trip-edit-section">
          {/* Pickup options */}
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0">Pickup Locations</Form.Label>
            <Button size="sm" onClick={addPickupOption} disabled={saving}>
              + Add Pickup
            </Button>
          </div>
          {form.pickupOptions?.length ? (
            <Accordion alwaysOpen className="mb-3 mt-2">
              {form.pickupOptions.map((option, idx) => (
                <Accordion.Item eventKey={`pickup-${idx}`} key={`pickup-${idx}`}>
                  <Accordion.Header>
                    <div className="d-flex flex-wrap align-items-center gap-2 w-100 pe-3">
                      <span className="fw-semibold">
                        {option.pickupLabel || `Pickup ${idx + 1}`}
                      </span>
                      {option.pickupValue ? (
                        <span className="small text-muted">{option.pickupValue}</span>
                      ) : null}
                      {option.isDefault ? <Badge bg="primary">Default</Badge> : null}
                      {option.isActive === false ? <Badge bg="secondary">Inactive</Badge> : null}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                  <Row>
                    <Col md={4} className="mb-2">
                      <Form.Label>Pickup Label</Form.Label>
                      <Form.Control
                        value={option.pickupLabel || ""}
                        onChange={(e) => {
                          updatePickupOption(idx, "pickupLabel", e.target.value);
                          if (!option.pickupValue) {
                            updatePickupOption(
                              idx,
                              "pickupValue",
                              slugify(e.target.value)
                            );
                          }
                        }}
                        placeholder="e.g., Hubli"
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4} className="mb-2">
                      <Form.Label>Pickup Value</Form.Label>
                      <Form.Control
                        value={option.pickupValue || ""}
                        onChange={(e) =>
                          updatePickupOption(idx, "pickupValue", e.target.value)
                        }
                        placeholder="e.g., hubli"
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4} className="mb-2">
                      <Form.Label>Order</Form.Label>
                      <Form.Control
                        type="number"
                        value={option.displayOrder ?? idx}
                        onChange={(e) =>
                          updatePickupOption(
                            idx,
                            "displayOrder",
                            Number(e.target.value)
                          )
                        }
                        disabled={saving}
                      />
                    </Col>
                  </Row>

                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <Form.Check
                      type="radio"
                      name="defaultPickup"
                      label="Default"
                      checked={Boolean(option.isDefault)}
                      onChange={() => setDefaultPickupOption(idx)}
                      disabled={saving}
                    />
                    <Form.Check
                      type="switch"
                      label="Active"
                      checked={option.isActive !== false}
                      onChange={(e) =>
                        updatePickupOption(idx, "isActive", e.target.checked)
                      }
                      disabled={saving}
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removePickupOption(idx)}
                      disabled={saving}
                    >
                      Delete Pickup
                    </Button>
                  </div>

                  <Form.Label>Overview</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="mb-2"
                    value={option.overviewText || ""}
                    onChange={(e) =>
                      updatePickupOption(idx, "overviewText", e.target.value)
                    }
                    disabled={saving}
                  />

                  <Row>
                    <Col md={4} className="mb-2">
                      <Form.Label>Departure Location</Form.Label>
                      <Form.Control
                        value={option.departureLocation || ""}
                        onChange={(e) =>
                          updatePickupOption(
                            idx,
                            "departureLocation",
                            e.target.value
                          )
                        }
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4} className="mb-2">
                      <Form.Label>Return Location</Form.Label>
                      <Form.Control
                        value={option.returnLocation || ""}
                        onChange={(e) =>
                          updatePickupOption(idx, "returnLocation", e.target.value)
                        }
                        disabled={saving}
                      />
                    </Col>
                    <Col md={4} className="mb-2">
                      <Form.Label>Departure Time</Form.Label>
                      <Form.Control
                        value={option.departureTime || ""}
                        onChange={(e) =>
                          updatePickupOption(idx, "departureTime", e.target.value)
                        }
                        disabled={saving}
                      />
                    </Col>
                  </Row>

                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="mb-0">Videos</Form.Label>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => addPickupVideo(idx)}
                          disabled={
                            saving ||
                            (option.videos || []).length >= VIDEO_LANGUAGES.length
                          }
                        >
                          + Add video
                        </Button>
                      </div>

                      {!(option.videos || []).length && (
                        <div className="text-muted small">
                          No video for this pickup location yet.
                        </div>
                      )}

                      {(option.videos || []).map((video, videoIdx) => {
                        const videoId = youtubeVideoId(video.youtubeUrl);
                        const badUrl = video.youtubeUrl?.trim() && !videoId;
                        const uploadKey = `${idx}-${videoIdx}`;
                        const usedLanguages = (option.videos || [])
                          .filter((_, i) => i !== videoIdx)
                          .map((other) => other.language);
                        return (
                          <div
                            key={videoIdx}
                            className="border rounded p-2 mb-2 bg-light"
                          >
                            <Row>
                              <Col md={3} className="mb-2">
                                <Form.Label>Language</Form.Label>
                                <Form.Select
                                  value={video.language || DEFAULT_VIDEO_LANGUAGE}
                                  onChange={(e) =>
                                    updatePickupVideo(
                                      idx,
                                      videoIdx,
                                      "language",
                                      e.target.value
                                    )
                                  }
                                  disabled={saving}
                                >
                                  {VIDEO_LANGUAGES.filter(
                                    (lang) =>
                                      !usedLanguages.includes(lang.code) ||
                                      lang.code === video.language
                                  ).map((lang) => (
                                    <option key={lang.code} value={lang.code}>
                                      {lang.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                              <Col md={9} className="mb-2">
                                <Form.Label>Title (optional)</Form.Label>
                                <Form.Control
                                  value={video.title || ""}
                                  placeholder="e.g. Pickup point walkthrough"
                                  onChange={(e) =>
                                    updatePickupVideo(
                                      idx,
                                      videoIdx,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                  disabled={saving}
                                />
                              </Col>
                            </Row>

                            <Form.Label>YouTube link</Form.Label>
                            <Form.Control
                              value={video.youtubeUrl || ""}
                              placeholder="https://youtu.be/... or https://www.youtube.com/watch?v=..."
                              isInvalid={Boolean(badUrl)}
                              onChange={(e) =>
                                updatePickupVideo(
                                  idx,
                                  videoIdx,
                                  "youtubeUrl",
                                  e.target.value
                                )
                              }
                              disabled={saving}
                            />
                            <Form.Control.Feedback type="invalid">
                              That does not look like a YouTube link.
                            </Form.Control.Feedback>
                            <div className="form-text">
                              Shown on the trip page and sent in Copy Quotation.
                            </div>

                            <Form.Label className="mt-2">
                              Downloadable video file (optional)
                            </Form.Label>
                            {video.downloadUrl ? (
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <a
                                  href={video.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="small text-truncate"
                                >
                                  {fileNameFromUrl(video.downloadUrl)}
                                </a>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    updatePickupVideo(
                                      idx,
                                      videoIdx,
                                      "downloadUrl",
                                      ""
                                    )
                                  }
                                  disabled={saving}
                                >
                                  Remove file
                                </Button>
                              </div>
                            ) : (
                              <Form.Control
                                type="file"
                                accept="video/mp4,video/webm,video/quicktime"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = "";
                                  if (file) {
                                    uploadPickupVideoFile(idx, videoIdx, file);
                                  }
                                }}
                                disabled={saving || uploadingVideoKey === uploadKey}
                              />
                            )}
                            {uploadingVideoKey === uploadKey && (
                              <div className="small text-muted mt-1">
                                <Spinner size="sm" animation="border" /> Uploading
                                video...
                              </div>
                            )}

                            <div className="mt-2">
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removePickupVideo(idx, videoIdx)}
                                disabled={saving}
                              >
                                Remove video
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </Card.Body>
                  </Card>

                  <Row>
                    <Col md={6}>
                      <PickupListEditor
                        label="Price Includes"
                        placeholder="Add an include item..."
                        items={option.priceIncludes || []}
                        onAdd={(value) =>
                          addPickupListItem(idx, "priceIncludes", value)
                        }
                        onUpdate={(itemIdx, value) =>
                          updatePickupListItem(
                            idx,
                            "priceIncludes",
                            itemIdx,
                            value
                          )
                        }
                        onRemove={(itemIdx) =>
                          removePickupListItem(idx, "priceIncludes", itemIdx)
                        }
                        onMove={(itemIdx, dir) =>
                          movePickupListItem(
                            idx,
                            "priceIncludes",
                            itemIdx,
                            dir
                          )
                        }
                        disabled={saving}
                      />
                    </Col>
                    <Col md={6}>
                      <PickupListEditor
                        label="Price Excludes"
                        placeholder="Add an exclude item..."
                        items={option.priceExcludes || []}
                        onAdd={(value) =>
                          addPickupListItem(idx, "priceExcludes", value)
                        }
                        onUpdate={(itemIdx, value) =>
                          updatePickupListItem(
                            idx,
                            "priceExcludes",
                            itemIdx,
                            value
                          )
                        }
                        onRemove={(itemIdx) =>
                          removePickupListItem(idx, "priceExcludes", itemIdx)
                        }
                        onMove={(itemIdx, dir) =>
                          movePickupListItem(
                            idx,
                            "priceExcludes",
                            itemIdx,
                            dir
                          )
                        }
                        disabled={saving}
                      />
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Form.Label className="mb-0">Itinerary</Form.Label>
                    <Button
                      size="sm"
                      onClick={() => addPickupDay(idx)}
                      disabled={saving}
                    >
                      + Add Day
                    </Button>
                  </div>
                  {option.itinerary?.length ? (
                    <ListGroup className="mb-3">
                      {option.itinerary.map((day, dayIdx) => (
                        <ListGroup.Item key={`pickup-${idx}-day-${dayIdx}`}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Control
                              style={{ maxWidth: 140 }}
                              value={day.day || ""}
                              onChange={(e) =>
                                updatePickupDayField(
                                  idx,
                                  dayIdx,
                                  "day",
                                  e.target.value
                                )
                              }
                              placeholder="Day 1"
                              disabled={saving}
                            />
                            <Form.Control
                              value={day.title || ""}
                              onChange={(e) =>
                                updatePickupDayField(
                                  idx,
                                  dayIdx,
                                  "title",
                                  e.target.value
                                )
                              }
                              placeholder="Title"
                              disabled={saving}
                            />
                            <div className="ms-auto d-flex gap-1">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={dayIdx === 0 || saving}
                                onClick={() => movePickupDay(idx, dayIdx, -1)}
                                title="Move day up"
                              >
                                <i className="bi bi-arrow-up" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={
                                  dayIdx === option.itinerary.length - 1 ||
                                  saving
                                }
                                onClick={() => movePickupDay(idx, dayIdx, 1)}
                                title="Move day down"
                              >
                                <i className="bi bi-arrow-down" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removePickupDay(idx, dayIdx)}
                                title="Delete day"
                                disabled={saving}
                              >
                                <i className="bi bi-x-lg" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>

                          <InputGroup className="mb-2">
                            <InputGroup.Text>Stay Info</InputGroup.Text>
                            <Form.Control
                              value={day.stayInfo || ""}
                              onChange={(e) =>
                                updatePickupDayField(
                                  idx,
                                  dayIdx,
                                  "stayInfo",
                                  e.target.value
                                )
                              }
                              placeholder="Optional stay details"
                              disabled={saving}
                            />
                          </InputGroup>

                          <PointsEditor
                            points={day.points || []}
                            onAdd={(txt) => addPickupPoint(idx, dayIdx, txt)}
                            onUpdate={(pointIdx, value) =>
                              updatePickupPoint(idx, dayIdx, pointIdx, value)
                            }
                            onRemove={(pointIdx) =>
                              removePickupPoint(idx, dayIdx, pointIdx)
                            }
                            onMove={(pointIdx, dir) =>
                              movePickupPoint(idx, dayIdx, pointIdx, dir)
                            }
                            disabled={saving}
                          />
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-muted small mb-3">
                      No days added yet.
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      <Form.Label className="mb-0">Day-Wise Plan</Form.Label>
                      <div className="small text-muted">
                        Pickup-specific internal driver tracking plan used in bookings.
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addPickupPlanDay(idx)}
                      disabled={saving}
                    >
                      + Add Day
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="ms-2"
                      onClick={() => copyDefaultPlanToPickup(idx)}
                      disabled={saving || !(form.dayWisePlanDays || []).length}
                    >
                      Use Default Day-Wise Plan
                    </Button>
                  </div>
                  {option.dayWisePlan?.length ? (
                    <ListGroup className="mb-3 mt-2">
                      {option.dayWisePlan.map((day, dayIdx) => (
                        <ListGroup.Item key={`pickup-${idx}-plan-day-${dayIdx}`}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <Form.Control
                              style={{ maxWidth: 140 }}
                              value={day.day || ""}
                              onChange={(e) =>
                                updatePickupPlanDayField(idx, dayIdx, "day", e.target.value)
                              }
                              placeholder="Day 1"
                              disabled={saving}
                            />
                            <Form.Control
                              value={day.title || ""}
                              onChange={(e) =>
                                updatePickupPlanDayField(idx, dayIdx, "title", e.target.value)
                              }
                              placeholder="Day title / route"
                              disabled={saving}
                            />
                            <div className="ms-auto d-flex gap-1">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={dayIdx === 0 || saving}
                                onClick={() => movePickupPlanDay(idx, dayIdx, -1)}
                                title="Move day up"
                              >
                                <i className="bi bi-arrow-up" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={dayIdx === option.dayWisePlan.length - 1 || saving}
                                onClick={() => movePickupPlanDay(idx, dayIdx, 1)}
                                title="Move day down"
                              >
                                <i className="bi bi-arrow-down" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removePickupPlanDay(idx, dayIdx)}
                                title="Delete day"
                                disabled={saving}
                              >
                                <i className="bi bi-x-lg" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="small mb-0">Locations</Form.Label>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => addPickupPlanLocation(idx, dayIdx)}
                              disabled={saving}
                            >
                              + Add Location
                            </Button>
                          </div>

                          {day.locations?.length ? (
                            <ListGroup>
                              {day.locations.map((location, locationIdx) => (
                                <ListGroup.Item
                                  key={`pickup-${idx}-plan-location-${dayIdx}-${locationIdx}`}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <Form.Control
                                    value={location.locationName || ""}
                                    onChange={(e) =>
                                      updatePickupPlanLocation(
                                        idx,
                                        dayIdx,
                                        locationIdx,
                                        "locationName",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Location name"
                                    disabled={saving}
                                  />
                                  <Form.Control
                                    value={location.googleMapLink || ""}
                                    onChange={(e) =>
                                      updatePickupPlanLocation(
                                        idx,
                                        dayIdx,
                                        locationIdx,
                                        "googleMapLink",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Google Map link"
                                    disabled={saving}
                                  />
                                  <Form.Control
                                    type="number"
                                    min={PLACE_CIRCLE_MIN}
                                    max={PLACE_CIRCLE_MAX}
                                    step={50}
                                    className="trip-plan-circle"
                                    value={location.arrivalRadiusM ?? ""}
                                    onChange={(e) =>
                                      updatePickupPlanLocation(idx, dayIdx, locationIdx, "arrivalRadiusM", e.target.value)
                                    }
                                    isInvalid={placeCircleInvalid(location.arrivalRadiusM)}
                                    placeholder="Circle m"
                                    title={PLACE_CIRCLE_HINT}
                                    aria-label="Own arrival circle in metres"
                                    disabled={saving}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={locationIdx === 0 || saving}
                                    onClick={() => movePickupPlanLocation(idx, dayIdx, locationIdx, -1)}
                                    title="Move location up"
                                  >
                                    <i className="bi bi-arrow-up" aria-hidden="true" />
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={locationIdx === day.locations.length - 1 || saving}
                                    onClick={() => movePickupPlanLocation(idx, dayIdx, locationIdx, 1)}
                                    title="Move location down"
                                  >
                                    <i className="bi bi-arrow-down" aria-hidden="true" />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removePickupPlanLocation(idx, dayIdx, locationIdx)}
                                    title="Delete location"
                                    disabled={saving}
                                  >
                                    <i className="bi bi-x-lg" aria-hidden="true" />
                                  </Button>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          ) : (
                            <div className="text-muted small">No locations added.</div>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-muted small mb-3">
                      No pickup-specific day-wise plan added yet.
                    </div>
                  )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <div className="text-muted small mb-3">
              No pickup locations added yet. The trip will use the base overview.
            </div>
          )}
            </section>
            </Tab.Pane>
            <Tab.Pane eventKey="photos" data-tab="photos">
            <section className="trip-edit-section">
          {/* Unified Images Manager */}
          <ImagesPanel
            form={form}
            getCategorySlug={getCategorySlug}
            presignAndUpload={presignAndUpload}
            sanitizeFilename={sanitizeFilename}
            mimeToExtUpload={mimeToExtUpload}
            persistFieldToDb={persistFieldToDb}
            persistGalleryArrayToDb={persistGalleryArrayToDb}
            openDownload={openDownload}
            deleteFromS3={deleteFromS3}
            disabled={saving}
            api={api}
          />

            </section>
            </Tab.Pane>
          </Tab.Content>
        </Modal.Body>
        <Modal.Footer>
          <div className="me-auto small text-muted">Save keeps the changes on every tab.</div>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
        </Tab.Container>
      </Form>
    </Modal>
  );
}

function PickupListEditor({
  label,
  placeholder,
  items,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
  disabled,
}) {
  const [newItem, setNewItem] = useState("");

  const add = () => {
    onAdd?.(newItem);
    setNewItem("");
  };

  return (
    <>
      <Form.Label>{label}</Form.Label>
      <InputGroup className="mb-2">
        <Form.Control
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !disabled) {
              e.preventDefault();
              add();
            }
          }}
          disabled={disabled}
        />
        <Button onClick={add} disabled={disabled}>
          + Add
        </Button>
      </InputGroup>
      {items?.length ? (
        <ListGroup className="mb-3">
          {items.map((item, itemIdx) => (
            <ListGroup.Item
              key={`${label}-${itemIdx}`}
              className="d-flex align-items-center gap-2"
            >
              <Form.Control
                value={item}
                onChange={(e) => onUpdate?.(itemIdx, e.target.value)}
                disabled={disabled}
              />
              <div className="d-flex gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={itemIdx === 0 || disabled}
                  onClick={() => onMove?.(itemIdx, -1)}
                  title="Move up"
                >
                  <i className="bi bi-arrow-up" aria-hidden="true" />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={itemIdx === items.length - 1 || disabled}
                  onClick={() => onMove?.(itemIdx, 1)}
                  title="Move down"
                >
                  <i className="bi bi-arrow-down" aria-hidden="true" />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onRemove?.(itemIdx)}
                  title="Delete"
                  disabled={disabled}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </Button>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <div className="text-muted small mb-3">No items added yet.</div>
      )}
    </>
  );
}

/* -------- Unified Images Panel (lists ALL S3 images for the trip) -------- */
function ImagesPanel({
  form,
  presignAndUpload,
  sanitizeFilename,
  mimeToExtUpload,
  persistFieldToDb,
  persistGalleryArrayToDb,
  openDownload,
  deleteFromS3,
  disabled,
  api,
}) {
  const [source, setSource] = React.useState([]); // all known images (DB + S3 listing)
  const [loadingList, setLoadingList] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);

  // per-item, per-action loading (icon | cover | gallery | delete)
  const [itemActionLoading, setItemActionLoading] = React.useState({}); // { [url]: {icon?:bool, cover?:bool, gallery?:bool, delete?:bool} }
  const setAction = (url, action, val) =>
    setItemActionLoading((prev) => ({
      ...prev,
      [url]: { ...(prev[url] || {}), [action]: val },
    }));

  // Build a unique set from current DB fields
  const buildLocalList = React.useCallback(() => {
    const setUrls = new Set();
    if (form.imageUrl) setUrls.add(form.imageUrl);
    if (form.coverUrl) setUrls.add(form.coverUrl);
    (form.galleryImages || []).forEach((u) => u && setUrls.add(u));
    return Array.from(setUrls);
  }, [form.imageUrl, form.coverUrl, form.galleryImages]);

  // --- List from S3 for this trip ---
  // We use {category_id}/{path}/ per your new scheme: app.s3.prefix/{category_id}/{path}
  const tryFetchFromPrefix = React.useCallback(async () => {
    const catId = form.categoryId;
    const tripPath = (form.path || "").trim();
    if (!catId || !tripPath) return [];

    // The backend will place this under "trips/<prefix>" in the bucket
    // if your list endpoint expects the FULL prefix including "trips/", use `trips/${catId}/${tripPath}/`
    const prefix = `${catId}/${tripPath}/`;

    try {
      setLoadingList(true);
      const { data } = await api.get("/api/admin/assets/list", {
        params: { prefix },
      });

      // Expecting public URLs back:
      const urls = Array.isArray(data?.items) ? data.items : [];

      // If your API returns KEYS instead of URLs, convert here:
      // const urls = (Array.isArray(data?.items) ? data.items : []).map(
      //   (key) => `https://iotrips-assets-prod.s3.us-east-1.amazonaws.com/trips/${key}`
      // );

      return urls;
    } catch {
      return [];
    } finally {
      setLoadingList(false);
    }
  }, [api, form.categoryId, form.path]);

  const refresh = React.useCallback(async () => {
    const local = buildLocalList();
    const fromPrefix = await tryFetchFromPrefix(); // may be empty
    const all = Array.from(new Set([...local, ...fromPrefix]));
    setSource(all);
  }, [buildLocalList, tryFetchFromPrefix]);

  // Refresh when modal opens with an item (Edit) or when cat/path change
  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.categoryId,
    form.path,
    form.imageUrl,
    form.coverUrl,
    form.galleryImages,
  ]);

  // ------------- Actions -------------
  const setAsIcon = async (url) => {
    try {
      setAction(url, "icon", true);
      await persistFieldToDb("imageUrl", url); // only one icon per trip
    } finally {
      setAction(url, "icon", false);
    }
  };

  const setAsCover = async (url) => {
    try {
      setAction(url, "cover", true);
      await persistFieldToDb("coverUrl", url); // only one cover per trip
    } finally {
      setAction(url, "cover", false);
    }
  };

  const isInGallery = (url) => (form.galleryImages || []).includes(url);

  const toggleGallery = async (url) => {
    try {
      setAction(url, "gallery", true);
      const arr = new Set(form.galleryImages || []);
      if (arr.has(url)) arr.delete(url);
      else arr.add(url);
      await persistGalleryArrayToDb(Array.from(arr));
    } finally {
      setAction(url, "gallery", false);
    }
  };

  const removeFromGalleryOnly = async (url) => {
    const arr = (form.galleryImages || []).filter((u) => u !== url);
    await persistGalleryArrayToDb(arr);
  };

  const onChooseMany = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles(files);
  };

  const handleUploadMany = async () => {
    if (!pendingFiles.length) return alert("Choose images first.");
    const catId = form.categoryId;
    const tripPath = (form.path || "").trim();
    if (!tripPath) return alert("Set the Trip Path first.");
    if (!catId) return alert("Choose a Category first.");

    // Upload to trips/{category_id}/{path}
    const location = `${catId}/${tripPath}`;

    try {
      setUploading(true);
      const uploadedUrls = [];
      for (const file of pendingFiles) {
        const safeName = sanitizeFilename(
          file.name,
          mimeToExtUpload(file.type)
        );
        const publicUrl = await presignAndUpload({
          file,
          location, // backend places under trips/<location>/<file>
          targetName: safeName,
        });
        uploadedUrls.push(publicUrl);
      }
      // By default, append to gallery
      const newArr = [...(form.galleryImages || []), ...uploadedUrls];
      await persistGalleryArrayToDb(newArr);
      setPendingFiles([]);
      await refresh();
    } catch (e) {
      console.error(e);
      alert(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const nameFromUrl = (url) => decodeURIComponent(url.split("/").pop() || "");

  return (
    <div className="vstack gap-2">
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-semibold">Images</div>
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            type="file"
            accept="image/*"
            multiple
            onChange={onChooseMany}
            disabled={uploading || disabled}
            style={{ maxWidth: 300 }}
          />
          <Button
            size="sm"
            variant="primary"
            onClick={handleUploadMany}
            disabled={uploading || !pendingFiles.length || disabled}
          >
            {uploading ? (
              <>
                <Spinner size="sm" className="me-2" /> Uploading…
              </>
            ) : (
              "Upload"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={refresh}
            disabled={loadingList || disabled}
            title="Reload images"
          >
            {loadingList ? (
              <>
                <Spinner size="sm" className="me-2" /> Loading…
              </>
            ) : (
              "Reload"
            )}
          </Button>
        </div>
      </div>

      {!form.categoryId || !form.path ? (
        <div className="text-muted small">
          Set both <strong>Category</strong> and <strong>Path</strong> to load
          images from S3.
        </div>
      ) : null}

      {source.length ? (
        <Row className="g-2">
          {source.map((url) => {
            const isCard = form.imageUrl === url;
            const isCover = form.coverUrl === url;
            const inGal = isInGallery(url);
            const name = nameFromUrl(url);
            const busy = itemActionLoading[url] || {};

            return (
              <Col md={4} sm={6} xs={12} key={url}>
                <div className="border rounded p-2 h-100 d-flex flex-column">
                  <div
                    className="ratio ratio-16x9 mb-2"
                    style={{ background: "#f8f9fa" }}
                  >
                    <img
                      src={url}
                      alt={name}
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                        borderRadius: 6,
                      }}
                    />
                  </div>
                  <div className="small text-truncate mb-2" title={name}>
                    {name}
                  </div>

                  {/* Status chips */}
                  <div className="mb-2 d-flex flex-wrap gap-1">
                    {isCard && <Badge bg="primary">Icon</Badge>}
                    {isCover && <Badge bg="info">Cover URL</Badge>}
                    {inGal && <Badge bg="secondary">In Gallery</Badge>}
                  </div>

                  <div className="mt-auto d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => openDownload(url)}
                      disabled={disabled}
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => navigator.clipboard?.writeText(url)}
                      disabled={disabled}
                    >
                      Copy URL
                    </Button>

                    {/* Set as Icon */}
                    <Button
                      size="sm"
                      variant={isCard ? "success" : "outline-success"}
                      onClick={() => setAsIcon(url)}
                      disabled={disabled || busy.icon}
                      title="Set as Icon (Card)"
                    >
                      {busy.icon ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Setting…
                        </>
                      ) : isCard ? (
                        "Set as Icon ✓"
                      ) : (
                        "Set as Icon"
                      )}
                    </Button>

                    {/* Set as Cover */}
                    <Button
                      size="sm"
                      variant={isCover ? "info" : "outline-info"}
                      onClick={() => setAsCover(url)}
                      disabled={disabled || busy.cover}
                      title="Set as Cover URL"
                    >
                      {busy.cover ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Setting…
                        </>
                      ) : isCover ? (
                        "Set as Cover ✓"
                      ) : (
                        "Set as Cover"
                      )}
                    </Button>

                    {/* Toggle gallery */}
                    <Button
                      size="sm"
                      variant={inGal ? "warning" : "outline-warning"}
                      onClick={() => toggleGallery(url)}
                      disabled={disabled || busy.gallery}
                      title="Toggle in Gallery"
                    >
                      {busy.gallery ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Updating…
                        </>
                      ) : inGal ? (
                        "Remove from Gallery"
                      ) : (
                        "Add to Gallery"
                      )}
                    </Button>

                    {/* Delete permanently */}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={async () => {
                        try {
                          setAction(url, "delete", true);
                          await deleteFromS3(url);
                          if (inGal) await removeFromGalleryOnly(url);
                          // If this file was used as icon/cover, clear those fields as needed:
                          // if (form.imageUrl === url) await persistFieldToDb("imageUrl", "");
                          // if (form.coverUrl === url) await persistFieldToDb("coverUrl", "");
                          await refresh();
                        } finally {
                          setAction(url, "delete", false);
                        }
                      }}
                      disabled={disabled || busy.delete}
                      title="Delete Permanently from S3"
                    >
                      {busy.delete ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Deleting…
                        </>
                      ) : (
                        "Delete Permanently"
                      )}
                    </Button>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      ) : (
        <div className="text-muted small">No images found.</div>
      )}
    </div>
  );
}

/* -------- Small child component: Points editor for a day -------- */
function PointsEditor({ points, onAdd, onUpdate, onRemove, onMove, disabled }) {
  const [newPoint, setNewPoint] = useState("");
  return (
    <div className="mt-2">
      <Form.Label className="small">Points</Form.Label>
      <InputGroup className="mb-2">
        <Form.Control
          placeholder="Add a point…"
          value={newPoint}
          onChange={(e) => setNewPoint(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !disabled) {
              e.preventDefault();
              onAdd?.(newPoint);
              setNewPoint("");
            }
          }}
          disabled={disabled}
        />
        <Button
          onClick={() => {
            onAdd?.(newPoint);
            setNewPoint("");
          }}
          disabled={disabled}
        >
          + Add
        </Button>
      </InputGroup>

      {points?.length ? (
        <ListGroup>
          {points.map((p, pi) => (
            <ListGroup.Item
              key={`pt-${pi}`}
              className="d-flex align-items-center gap-2"
            >
              <Form.Control
                value={p}
                onChange={(e) => onUpdate?.(pi, e.target.value)}
                disabled={disabled}
              />
              <div className="d-flex gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pi === 0 || disabled}
                  onClick={() => onMove?.(pi, -1)}
                  title="Move up"
                >
                  ↑
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={pi === points.length - 1 || disabled}
                  onClick={() => onMove?.(pi, 1)}
                  title="Move down"
                >
                  ↓
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onRemove?.(pi)}
                  title="Delete"
                  disabled={disabled}
                >
                  ✕
                </Button>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <div className="text-muted small">No points added yet.</div>
      )}
    </div>
  );
}
